import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.string.includes.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import { useCallback, useReducer, useMemo } from 'react';
import uniq from 'utils/uniq';
import groupBy from 'utils/groupBy';
import { isObjectWithId, isIdType } from 'types/utils';
var EMPTY_ARRAY = [];
var defaultState = {
  itemsToAdd: EMPTY_ARRAY,
  itemsToRemove: EMPTY_ARRAY
};
function defaultGetItemId(item) {
  if (isIdType(item)) {
    return item;
  }
  if (isObjectWithId(item)) {
    return item.id;
  }
  throw Error("useComputedItems: Can't determine item id. Please provide a valid `getItemId` function.");
}
export function computeItems(_ref) {
  var _ref$getItemId = _ref.getItemId,
    getItemId = _ref$getItemId === void 0 ? defaultGetItemId : _ref$getItemId,
    itemsToRemove = _ref.itemsToRemove,
    initialItems = _ref.initialItems,
    itemsToAdd = _ref.itemsToAdd;
  var itemIdsToRemove = itemsToRemove.map(function (item) {
    return getItemId(item);
  });
  return uniq([].concat(_toConsumableArray(initialItems), _toConsumableArray(itemsToAdd)).filter(function (item) {
    return !itemIdsToRemove.includes(getItemId(item));
  }), getItemId);
}

/**
 * A hook for managing a list of generic items when you need to
 * separately track added and/or removed items.
 *
 * The returned `computedItems` array contains the initially provided
 * items with the added items added, and removed items removed.
 *
 * Note that you can't remove items that don't already exist in the
 * `initialItems` array, but you can add items that already exist in it.
 * This behaviour can be customised using the `allowAddingExistingItems`
 * option.
 *
 * Duplicates are removed from all returned arrays.
 */

export default function useComputedItems(providedOptions) {
  var _ref2 = _objectSpread({
      initialItems: EMPTY_ARRAY,
      getItemId: defaultGetItemId,
      allowAddingExistingItems: true
    }, providedOptions),
    initialItems = _ref2.initialItems,
    getItemId = _ref2.getItemId,
    allowAddingExistingItems = _ref2.allowAddingExistingItems;
  var initialItemIds = useMemo(function () {
    return initialItems.map(function (item) {
      return getItemId(item);
    });
  }, [getItemId, initialItems]);
  function stateReducer(state, action) {
    var prevItemsToAdd = state.itemsToAdd,
      prevItemsToRemove = state.itemsToRemove;
    switch (action.type) {
      case 'add':
        {
          var _itemsToAdd = uniq([].concat(_toConsumableArray(prevItemsToAdd), _toConsumableArray(action.items)), getItemId);

          // Items that were added should be removed from `itemsToRemove`
          var itemsToAddIds = _itemsToAdd.map(function (item) {
            return getItemId(item);
          });
          var _itemsToRemove = prevItemsToRemove.filter(function (item) {
            return !itemsToAddIds.includes(getItemId(item));
          });
          if (!allowAddingExistingItems) {
            _itemsToAdd = _itemsToAdd.filter(function (item) {
              return !initialItemIds.includes(getItemId(item));
            });
          }
          return {
            itemsToAdd: _itemsToAdd,
            itemsToRemove: _itemsToRemove
          };
        }
      case 'remove':
        {
          // Don't remove items that don't exist in the initial items
          var prevItemIdsToAdd = prevItemsToAdd.map(function (item) {
            return getItemId(item);
          });
          // Group items: Those that should be removed from the
          // initial items, those that should just be removed from
          // the "to be added" items, and those that should be removed
          // from both.
          // Items that don't match those three groups will be ignored.
          var groupedItems = groupBy(action.items, function (item) {
            var itemId = getItemId(item);
            var isIncludedInInitialItems = initialItemIds.includes(itemId);
            var isIncludedInItemsToAdd = prevItemIdsToAdd.includes(itemId);
            if (isIncludedInInitialItems && isIncludedInItemsToAdd) {
              return 'removeFromBoth';
            }
            if (isIncludedInInitialItems) {
              return 'removeFromInitial';
            }
            if (isIncludedInItemsToAdd) {
              return 'removeFromAdded';
            }
            return 'ignore';
          });
          var _itemsToRemove2 = uniq([].concat(_toConsumableArray(prevItemsToRemove), _toConsumableArray(groupedItems.removeFromInitial || EMPTY_ARRAY), _toConsumableArray(groupedItems.removeFromBoth || EMPTY_ARRAY)), getItemId);
          var itemIdsToRemoveFromAdded = [].concat(_toConsumableArray(groupedItems.removeFromAdded || EMPTY_ARRAY), _toConsumableArray(groupedItems.removeFromBoth || EMPTY_ARRAY)).map(function (item) {
            return getItemId(item);
          });
          var _itemsToAdd2 = prevItemsToAdd.filter(function (item) {
            return !itemIdsToRemoveFromAdded.includes(getItemId(item));
          });
          return {
            itemsToRemove: _itemsToRemove2,
            itemsToAdd: _itemsToAdd2
          };
        }
      case 'remove-all':
        return {
          itemsToAdd: EMPTY_ARRAY,
          itemsToRemove: initialItems
        };
      case 'reset':
        return defaultState;
      default:
        return state;
    }
  }
  var _useReducer = useReducer(stateReducer, defaultState),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    _useReducer2$ = _useReducer2[0],
    itemsToAdd = _useReducer2$.itemsToAdd,
    itemsToRemove = _useReducer2$.itemsToRemove,
    dispatch = _useReducer2[1];
  var wasChanged = itemsToAdd.length > 0 || itemsToRemove.length > 0;
  var addItem = useCallback(function (itemToAdd) {
    dispatch({
      type: 'add',
      items: Array.isArray(itemToAdd) ? itemToAdd : [itemToAdd]
    });
  }, []);
  var removeItem = useCallback(function (itemToRemove) {
    dispatch({
      type: 'remove',
      items: Array.isArray(itemToRemove) ? itemToRemove : [itemToRemove]
    });
  }, []);
  var removeAllItems = useCallback(function () {
    dispatch({
      type: 'remove-all'
    });
  }, []);
  var reset = useCallback(function () {
    dispatch({
      type: 'reset'
    });
  }, []);
  var computedItems = useMemo(function () {
    return computeItems({
      itemsToRemove: itemsToRemove,
      getItemId: getItemId,
      initialItems: initialItems,
      itemsToAdd: itemsToAdd
    });
  }, [getItemId, initialItems, itemsToAdd, itemsToRemove]);
  return {
    computedItems: computedItems,
    itemsToAdd: itemsToAdd,
    itemsToRemove: itemsToRemove,
    wasChanged: wasChanged,
    addItem: addItem,
    removeItem: removeItem,
    removeAllItems: removeAllItems,
    reset: reset
  };
}